// Backlog #332 (2026-10-12): the app's translations (i18next + react-i18next).
//
// Keys are semantic ("projeler.kaydet"), one JSON file per namespace and language under
// src/locales/<lang>/<namespace>.json, listed in src/locales/index.js. Turkish is the default and
// the fallback: a key missing from en shows the Turkish text. Both files must carry every key.
//
// In a component:   const { t } = useTranslation("portal");   t("projeler.baslik")
// Outside one:      import i18n from "../i18n";   i18n.t("portal:projeler.baslik")
//                   (only at render time -- a module-level constant would freeze one language)
//
// The selected language lives in LanguageContext (localStorage "appLanguage"), which calls
// i18n.changeLanguage. getAuthHeader() / getPublicHeader() send it to the backend as
// Accept-Language, which picks the language of names that come from the database (backend #330).
import axios from "axios";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { resources, namespaces } from "./locales";

export const LANGUAGES = ["tr", "en"];
// language_id in the backend (LanguageConstant): 1 = TR, 2 = EN.
export const LANGUAGE_IDS = { tr: 1, en: 2 };
export const STORAGE_KEY = "appLanguage";

const storedLanguage = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return LANGUAGES.includes(v) ? v : null;
  } catch (e) {
    return null;
  }
};

i18n.use(initReactI18next).init({
  resources,
  ns: namespaces,
  defaultNS: "common",
  lng: storedLanguage() || "tr",
  fallbackLng: "tr",
  supportedLngs: LANGUAGES,
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
  react: { useSuspense: false },
});

/** "tr" or "en" -- the language the UI shows right now. */
export const currentLanguage = () => (LANGUAGES.includes(i18n.language) ? i18n.language : "tr");

/** 1 or 2 -- the backend's language_id for the language the UI shows right now. */
export const currentLanguageId = () => LANGUAGE_IDS[currentLanguage()];

/** "tr-TR" or "en-GB" -- for toLocaleString / toLocaleDateString on pages that follow the UI language. */
export const currentLocaleTag = () => (currentLanguage() === "en" ? "en-GB" : "tr-TR");

// Every axios request carries the UI language, including the ones whose headers are built by hand
// instead of through getAuthHeader() / getPublicHeader() (BetterStands, saha forms...). A header the
// caller set itself is left alone. Without this the browser sends its own language (the OS's), and
// the backend would answer in it.
axios.interceptors.request.use((config) => {
  const headers = config.headers || {};
  const has = typeof headers.has === "function" ? headers.has("Accept-Language") : headers["Accept-Language"];
  if (!has) {
    if (typeof headers.set === "function") headers.set("Accept-Language", currentLanguage());
    else headers["Accept-Language"] = currentLanguage();
    config.headers = headers;
  }
  return config;
});

export default i18n;
